import { Injectable, inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { APP_CONSTANTS } from '@app/app.constants';
import { EStorageKey } from '@app/core/api/auth/auth.enum';
import { SentryFacade } from '@app/core/sentry';
import {
  getErrorMessageFromUnknown,
  getMessageFromErrorEvent,
  messageLooksLikeChunkLoadFailure,
} from '@app/shared/helpers';
import {
  CHUNK_LOAD_FAILURE_EVENT,
  isChunkLoadRecoveryEnabledForEnvironment,
} from '@app/core/utils/chunk-load-error.util';
import { environment } from '../../../environments/environment';
import { ConfirmDialogService } from './confirm-dialog.service';
import { StorageService } from './storage.service';

/**
 * Recovers from stale lazy chunks after deploy (Sentry INLIGHTENED-FE-V / dynamic import failures on QA).
 *
 * @remarks
 * **Manual testing**
 * 1. Non-prod: open the app with `?testChunkRecovery=1` — confirms the Material confirm dialog path.
 * 2. After the app has bootstrapped, run:
 *    `window.dispatchEvent(new CustomEvent('inlightened-chunk-load-failure'))`
 *    On deployed builds (staging/preprod/prod), expect one auto-reload attempt and then a reload prompt fallback (see localStorage keys).
 * 3. DevTools → Network: block `.js` requests matching lazy chunks (e.g. pattern `chunk-`), then navigate to a lazy route — should trigger navigation or import failure handling.
 * 4. Sentry: new sessions include tag `chunk_recovery_handler=enabled`. Chunk-like errors get `dynamic_import_chunk_failure=true`. After the prompt, breadcrumb `chunk_recovery` and tag `chunk_load_recovery_state=prompt_shown` (when DSN is configured).
 */
@Injectable({
  providedIn: 'root',
})
export class ChunkLoadRecoveryService {
  private router = inject(Router);
  private confirmDialog = inject(ConfirmDialogService);
  private sentryFacade = inject(SentryFacade);
  private storage = inject(StorageService);

  private get promptStorageKey(): string {
    return `${EStorageKey.ChunkLoadRecoveryPrompted}:${APP_CONSTANTS.VERSION}`;
  }

  private get reloadStorageKey(): string {
    return `${EStorageKey.ChunkLoadRecoveryReloadAttempted}:${APP_CONSTANTS.VERSION}`;
  }

  private alreadyPromptedForCurrentVersion(): boolean {
    return this.storage.getBoolean(this.promptStorageKey);
  }

  private markPromptShownForCurrentVersion(): void {
    this.storage.setBoolean(this.promptStorageKey, true);
  }

  private wasReloadAttemptedForCurrentVersion(): boolean {
    return this.storage.getBoolean(this.reloadStorageKey);
  }

  private markReloadAttemptedForCurrentVersion(): void {
    this.storage.setBoolean(this.reloadStorageKey, true);
  }

  private recordSentryRecoveryState(
    state: 'reload_attempted' | 'prompt_shown' | 'reload_confirmed'
  ): void {
    try {
      this.sentryFacade.trackChunkRecoveryState(state);
    } catch {
      // ignore
    }
  }

  private showRecoveryDialog(skipMarkShown = false): void {
    if (!skipMarkShown) {
      this.markPromptShownForCurrentVersion();
    }
    this.recordSentryRecoveryState('prompt_shown');
    this.confirmDialog
      .open({
        title: 'New version available',
        message:
          'Part of the app could not be loaded. This may be due to a recent update. Reload the page?',
        applyBtn: 'Reload',
        cancelBtn: 'Not now',
        panelClass: ['dialog-panel', 'dialog-panel-confirm'],
      })
      .subscribe((reload) => {
        if (reload) {
          this.recordSentryRecoveryState('reload_confirmed');
          window.location.reload();
        }
      });
  }

  private handleChunkLoadError(): void {
    if (!isChunkLoadRecoveryEnabledForEnvironment(environment)) {
      return;
    }
    if (!this.wasReloadAttemptedForCurrentVersion()) {
      this.markReloadAttemptedForCurrentVersion();
      this.recordSentryRecoveryState('reload_attempted');
      window.location.reload();
      return;
    }

    if (this.alreadyPromptedForCurrentVersion()) {
      return;
    }
    this.showRecoveryDialog();
  }

  /** Call from APP_INITIALIZER to start listening for navigation and unhandled chunk load errors. */
  init(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener(CHUNK_LOAD_FAILURE_EVENT, () => {
      this.handleChunkLoadError();
    });

    this.router.events
      .pipe(filter((e): e is NavigationError => e instanceof NavigationError))
      .subscribe((event) => {
        const message = getErrorMessageFromUnknown(event.error);
        if (messageLooksLikeChunkLoadFailure(message)) {
          this.handleChunkLoadError();
        }
      });

    window.addEventListener('unhandledrejection', (event) => {
      const message = getErrorMessageFromUnknown(event.reason);
      if (messageLooksLikeChunkLoadFailure(message)) {
        event.preventDefault();
        this.handleChunkLoadError();
      }
    });

    window.addEventListener(
      'error',
      (event) => {
        const message = getMessageFromErrorEvent(event);
        if (messageLooksLikeChunkLoadFailure(message)) {
          this.handleChunkLoadError();
        }
      },
      true
    );

    // Local testing: open dialog when URL has ?testChunkRecovery=1 (non-prod only)
    if (
      environment.name !== 'prod' &&
      window.location.search.includes('testChunkRecovery=1')
    ) {
      this.showRecoveryDialog(true);
    }
  }
}
